import { useState, useEffect } from 'react';
import { createApiClient, type UserDataCounts } from '@n8n-builder/api-client';
import { useChatStore } from '../../store/chat';
import { useWorkflowStore } from '../../store/workflow';
import { API_BASE_URL } from '../../lib/api-config';

function SpinnerIcon({ className }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}

function ExclamationTriangleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

interface DeleteSectionProps {
  title: string;
  description: string;
  count: number;
  itemLabel: string;
  confirmPhrase: string;
  onDelete: () => Promise<void>;
  isDeleting: boolean;
}

function DeleteSection({
  title,
  description,
  count,
  itemLabel,
  confirmPhrase,
  onDelete,
  isDeleting,
}: DeleteSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const isConfirmValid = inputValue === confirmPhrase;

  const handleDelete = async () => {
    setError(null);
    try {
      await onDelete();
      setSuccess(true);
      setInputValue('');
      setIsExpanded(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  if (count === 0) {
    return (
      <div className="border border-gray-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 p-2 bg-gray-100 rounded-lg">
            <ExclamationTriangleIcon className="h-5 w-5 text-gray-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500 mt-1">No {itemLabel} to delete.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-red-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-6 flex items-start gap-4 text-left hover:bg-red-50/50 transition-colors"
      >
        <div className="flex-shrink-0 p-2 bg-red-100 rounded-lg">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-medium text-gray-900">{title}</h3>
          <p className="text-sm text-gray-500 mt-1">{description}</p>
          <p className="text-sm text-red-600 font-medium mt-2">
            {count} {itemLabel} will be permanently deleted
          </p>
        </div>
        <ChevronDownIcon
          className={`h-5 w-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
        />
      </button>

      {isExpanded && (
        <div className="px-6 pb-6 pt-2 border-t border-red-200 bg-red-50/30">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <div className="flex gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-yellow-800">Warning</h4>
                <p className="text-sm text-yellow-700 mt-1">
                  This action cannot be undone. All your {itemLabel} will be permanently deleted.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              Type <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded text-red-600">{confirmPhrase}</span> to confirm
            </label>
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={confirmPhrase}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
              disabled={isDeleting}
            />

            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
                {error}
              </div>
            )}

            {success && (
              <div className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
                Successfully deleted all {itemLabel}
              </div>
            )}

            <button
              onClick={handleDelete}
              disabled={!isConfirmValid || isDeleting}
              className="w-full rounded-lg bg-red-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-all duration-200 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isDeleting ? (
                <>
                  <SpinnerIcon className="h-4 w-4" />
                  Deleting...
                </>
              ) : (
                `Delete All ${title.replace('Delete All ', '')}`
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function DangerZoneTab() {
  const [dataCounts, setDataCounts] = useState<UserDataCounts | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeletingChats, setIsDeletingChats] = useState(false);
  const [isDeletingWorkflows, setIsDeletingWorkflows] = useState(false);

  const { setConversations, startNewConversation } = useChatStore();
  const { setWorkflows } = useWorkflowStore();

  const getApiClient = () => {
    return createApiClient(API_BASE_URL);
  };

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const client = getApiClient();
        const response = await client.getDataCounts();
        if (response.data) {
          setDataCounts(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch data counts:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCounts();
  }, []);

  const handleDeleteAllChats = async () => {
    setIsDeletingChats(true);
    try {
      const client = getApiClient();
      const response = await client.deleteAllConversations('DELETE ALL CHATS');
      if (response.error) {
        throw new Error(response.error);
      }

      // Update local state
      setConversations([]);
      startNewConversation();

      // Update counts
      setDataCounts((prev) => prev ? { ...prev, conversationCount: 0 } : null);
    } finally {
      setIsDeletingChats(false);
    }
  };

  const handleDeleteAllWorkflows = async () => {
    setIsDeletingWorkflows(true);
    try {
      const client = getApiClient();
      const response = await client.deleteAllWorkflows('DELETE ALL WORKFLOWS');
      if (response.error) {
        throw new Error(response.error);
      }

      // Update local state
      setWorkflows([]);

      // Update counts
      setDataCounts((prev) => prev ? { ...prev, workflowCount: 0 } : null);
    } finally {
      setIsDeletingWorkflows(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <SpinnerIcon className="h-8 w-8 text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Danger Zone</h2>
        <p className="text-sm text-gray-500">
          Irreversible actions that will permanently delete your data.
        </p>
      </div>

      <div className="space-y-4">
        <DeleteSection
          title="Delete All Chats"
          description="Permanently delete all your conversations and associated chat history."
          count={dataCounts?.conversationCount ?? 0}
          itemLabel="chats"
          confirmPhrase="DELETE ALL CHATS"
          onDelete={handleDeleteAllChats}
          isDeleting={isDeletingChats}
        />

        <DeleteSection
          title="Delete All Workflows"
          description="Permanently delete all your saved workflows."
          count={dataCounts?.workflowCount ?? 0}
          itemLabel="workflows"
          confirmPhrase="DELETE ALL WORKFLOWS"
          onDelete={handleDeleteAllWorkflows}
          isDeleting={isDeletingWorkflows}
        />
      </div>
    </div>
  );
}
